// Console Add/Edit-Agent authoring helpers (fn-71 / ADR 0035 / R11).
//
// Pure, unit-tested logic for the admin agent-authoring form: the converter
// from the `<AgentToolConfigPanel>` `authoring` output (`AuthoringToolConfig`)
// to the MA-native `tools[]` the `POST`/`PATCH /agents` body carries, plus the
// readers that pre-fill the Edit form from an existing agent's
// `config_snapshot`. Kept side-effect-free so the form's network/React shell
// (`agent-form.tsx`) stays thin and these can be tested without mounting.
//
// **Wire shape** — `ConfigurableToolsResponse` is owned by
// `@sb-ai-platform/schemas` and surfaced through the BFF
// `agents.draftConfigurableTools` / `agents.configurableTools` procedures. We
// derive it from the router output (like `agents/list.tsx` does for its rows)
// rather than adding a direct `schemas` dependency to this SPA, so a contract
// change is a compile error here, not a silent runtime drift.

import type { BffRouter } from '@sb-ai-platform/bff';
import {
  type AuthoringToolConfig,
  type ToolConfigPanelState,
  type ToolSelection,
  defaultPanelState,
} from '@sb-ai-platform/ui-react';
import type { inferRouterOutputs } from '@trpc/server';

export type ConfigurableToolsResponse =
  inferRouterOutputs<BffRouter>['agents']['draftConfigurableTools'];

/**
 * The MA-native built-in toolset discriminator (`agent_toolset_20260401`). One
 * such entry carries the native-built-in toggles. Mirrors the control plane's
 * `BUILT_IN_TOOLSET_TYPE` (`configurable-tools.ts`).
 */
export const AGENT_BUILTIN_TOOLSET_TYPE = 'agent_toolset_20260401' as const;

/** Shared empty direct-server-id set — the default "no direct connectors" arg. */
const EMPTY_SERVER_IDS: ReadonlySet<string> = new Set<string>();

// Agent-name slug — mirrors `AGENT_NAME_SLUG` in `@sb-ai-platform/schemas`. The
// control plane is authoritative (it re-validates the body); this is only the
// friendly client-side hint so the author isn't surprised by a 400.
const AGENT_NAME_SLUG = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;

/** `true` when `name` matches the agent-name slug the control plane enforces. */
export function isValidAgentName(name: string): boolean {
  return AGENT_NAME_SLUG.test(name);
}

/**
 * Split a namespaced gateway tool id (`<target>___<tool>`) into its bare tool
 * name. Defensive fallback when a `tool_id` isn't found in the catalog index;
 * returns the input unchanged when there is no `___` separator.
 */
export function bareToolName(id: string): string {
  const sep = id.lastIndexOf('___');
  return sep < 0 ? id : id.slice(sep + 3);
}

/**
 * Convert the panel's `authoring` output (`AuthoringToolConfig`) → MA-native
 * `tools[]` (the fn-61 authz-bearing shape) — the SAME shape the control
 * plane's `draftToolSelectionToAuthoredTools` produces, so the shared resolver
 * qualifies the `permission_policy` names and resolves the tool scope from
 * exactly this shape.
 *
 * Shape contract (mirrors the control-plane converter):
 *   - ONE `agent_toolset_20260401` entry — `configs: [{ name, enabled }]` when
 *     any built-in toggle is present, else the bare default toolset.
 *   - One `mcp_toolset` entry per ENABLED connector, ALLOW-LIST mode
 *     (`default_config.enabled = false`): each non-disabled tool is
 *     `{ name, enabled: true }`; a non-`always_allow` permission is carried as
 *     `permission_policy: { type }` (`always_allow` is the default — omitted, so
 *     an all-allow agent needs no gateway qualification).
 *
 * **fn-72 (R5) — QUALIFIED names are preserved VERBATIM.** The panel keys its
 * authoring output by the QUALIFIED federated `tool_id` (`<target>___<tool>`),
 * and with the whole-gateway view ONE `smokeball` connector surfaces tools across
 * MANY targets. We persist that qualified `tool_id` as the `configs[].name`
 * (NEVER collapsing it to its bare suffix), so two tools with COLLIDING bare
 * names under different targets — `smokeball___get_matter` vs `forms___get_matter`
 * — stay distinct grants (collapsing them would merge two grants into one AND
 * silently widen scope). `isToolAllowed` matches a qualified config by its parsed
 * `(target, tool)` pair, and `ownedTargets = {serverId} ∪ {qualified config
 * targets}` grants every target via the ONE registered `smokeball` descriptor.
 *
 * **Denylist (R7) is the parsed BARE suffix.** The session-scope denylist is
 * bare-name based, so it is checked on the parsed suffix — NOT the qualified
 * name (which would never match). That clamp is applied UPSTREAM of this
 * converter: the panel projects each tool's `denylist_locked` (computed
 * server-side over the full denylist) and `resolveAuthoringToolPermission`
 * forces a denylisted tool to `always_ask`, so a denylisted tool can never reach
 * this converter as `always_allow`; the control plane re-validates the same
 * denylist authoritatively on the write path.
 *
 * **Catalog-unavailable connectors** emit a WHOLE-SERVER grant (a bare
 * `mcp_toolset`, default-on) NOT an empty allow-list — their tools are UNKNOWN,
 * not empty, so an empty `configs[]` would silently deny every tool on them
 * (the `catalogUnavailable` flag the panel surfaces). A fully-DISABLED
 * connector is dropped (its tools are out of scope).
 *
 * Pure; blank server / tool names are dropped.
 */
/**
 * Merge the gateway+direct panel output with the separately-tracked PLAIN
 * non-gateway connector selections into the single MA-native `tools[]` the
 * `POST`/`PATCH /agents` body carries (fn-73 R3 / fn-74 R5).
 *
 * The gateway connector AND any selected DIRECT (`supports_direct_introspection`)
 * connector (e.g. InfoTrack) are authored through the `<AgentToolConfigPanel>`
 * (the direct connector reaches it via `direct_mcp_servers` on the draft query)
 * and serialized by {@link authoringConfigToTools} — exactly ONE `mcp_toolset`
 * per connector: a per-tool allow-list when introspected, or the fn-73 whole-server
 * `always_ask` grant when login-required/degraded.
 *
 * PLAIN non-gateway connectors (`nonGatewaySelections`) — a non-gateway connector
 * that is NOT `supports_direct_introspection` — are NOT routed through the panel
 * (the platform can't enumerate a direct-url server's tools and can't denylist-
 * check them), so each selected one is persisted as ONE whole-server `mcp_toolset`
 * LOCKED to `always_ask`:
 *   `{ type:'mcp_toolset', mcp_server_name:<server>, configs:[],
 *      default_config:{ enabled:true, permission_policy:{ type:'always_ask' } } }`
 *
 * **`nonGatewaySelections` MUST exclude every DIRECT server** (the caller routes
 * those through `direct_mcp_servers` → the panel → `authoringConfigToTools`); a
 * direct server here would double-emit its grant. `directServerIds` is forwarded
 * to {@link authoringConfigToTools} so a login-required/degraded direct connector's
 * fallback is the `always_ask` grant (not a bare default-on one).
 *
 * The native-built-in toolset comes from the panel output (it always emits one).
 * The panel `mcp_toolset` entries (gateway + direct) come first, then the plain
 * non-gateway whole-server grants, in selection order. Blank names are dropped; a
 * plain name colliding with a panel-emitted connector is skipped (the panel's
 * entry wins) so a connector is never emitted twice.
 *
 * Pure.
 */
export function buildAgentTools(input: {
  readonly gatewayConfig: AuthoringToolConfig;
  readonly nonGatewaySelections: readonly string[];
  /** The `mcp_server_name`s that are DIRECT connectors (fn-74). Default empty. */
  readonly directServerIds?: ReadonlySet<string>;
}): Record<string, unknown>[] {
  const directServerIds = input.directServerIds ?? EMPTY_SERVER_IDS;
  const tools = authoringConfigToTools(input.gatewayConfig, directServerIds);
  // The panel already emitted a grant for every gateway + direct connector; a
  // plain whole-server grant must never collide with one of those.
  const seen = new Set<string>();
  for (const entry of tools) {
    if (entry.type === 'mcp_toolset' && typeof entry.mcp_server_name === 'string') {
      seen.add(entry.mcp_server_name);
    }
  }
  for (const raw of input.nonGatewaySelections) {
    const serverId = raw.trim();
    // Blank, duplicate, OR already emitted by the panel (a direct connector that
    // slipped into `nonGatewaySelections`) → skip; never double-emit a grant.
    if (serverId.length === 0 || seen.has(serverId)) continue;
    seen.add(serverId);
    tools.push({
      type: 'mcp_toolset',
      mcp_server_name: serverId,
      // Whole-server grant: the platform can't enumerate this server's tools,
      // so there is no per-tool allow-list — every tool is in scope, LOCKED to
      // `always_ask` (HITL on every call; denylist-safe).
      configs: [],
      default_config: { enabled: true, permission_policy: { type: 'always_ask' } },
    });
  }
  return tools;
}

/**
 * Read the saved `mcp_toolset` entries from an agent's `config_snapshot.tools[]`,
 * keyed by `mcp_server_name`. Built-in toolsets are skipped (the form always
 * emits its own). Blank / duplicate server names are dropped (first wins, order
 * preserved). Returns the entries VERBATIM so an edit can re-emit a saved
 * connector's grant byte-for-byte if the form can't currently represent it.
 */
export function readSavedMcpToolsets(
  snapshot: Record<string, unknown> | null,
): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  const tools = snapshot === null ? null : snapshot.tools;
  if (!Array.isArray(tools)) return out;
  for (const entry of tools) {
    const record = asRecord(entry);
    if (record === null || record.type !== 'mcp_toolset') continue;
    const name = typeof record.mcp_server_name === 'string' ? record.mcp_server_name.trim() : '';
    if (name.length === 0 || out.has(name)) continue;
    out.set(name, record);
  }
  return out;
}

/**
 * Build the submit `tools[]` for an EDIT, preserving (never stripping) a saved
 * connector's persisted scope when the form can't currently represent it (fn-73
 * — CodeRabbit data-integrity fix).
 *
 * THE BUG this guards against: the form rebuilds `tools[]` purely from current
 * FORM STATE, and that state is seeded only from connectors present + selectable
 * in the live `mcpServers.list`. A SAVED connector that is currently missing from
 * the list OR `selectable:false` (a direct-url server that went down, or a gateway
 * whose row vanished) would otherwise seed "off" and an unrelated edit-save would
 * SILENTLY STRIP its persisted `mcp_toolset`.
 *
 * FIX — partition the agent's ORIGINAL saved `mcp_toolset` entries:
 *   - **Representable** (`mcp_server_name ∈ representableServerNames`) → driven by
 *     current FORM STATE via {@link buildAgentTools} (the user's toggles). This
 *     covers the present+selectable gateway connector (its own name AND every
 *     federated target it folds in, fn-72) and every present+selectable
 *     non-gateway connector.
 *   - **Not currently representable** → its saved entry is PRESERVED VERBATIM, so
 *     an edit never drops it. (Form output still WINS for any server name it
 *     emits — a representable connector's current state, never a stale preserve.)
 *
 * `representableServerNames` is the source of truth for the partition, NOT the raw
 * form selections: a connector that became non-selectable after a refetch is
 * excluded from the set, so even if stale form state still references it, its
 * grant falls into the preserved-original bucket (or is dropped if it was never
 * saved) rather than being submitted from stale state (fn-73 finding 3).
 *
 * Dedupe is by `mcp_server_name`: a server emitted by the form output is never
 * also preserved. Pure.
 */
export function buildAgentToolsForEdit(input: {
  readonly gatewayConfig: AuthoringToolConfig;
  readonly nonGatewaySelections: readonly string[];
  /** The agent's saved `mcp_toolset` entries, keyed by `mcp_server_name`. */
  readonly savedToolsets: ReadonlyMap<string, Record<string, unknown>>;
  /** Server names the form can currently drive (gateway + folded targets + non-gateway rows). */
  readonly representableServerNames: ReadonlySet<string>;
  /** The `mcp_server_name`s that are DIRECT connectors (fn-74). Default empty. */
  readonly directServerIds?: ReadonlySet<string>;
}): Record<string, unknown>[] {
  // Form-derived entries for the representable connectors (the user's toggles).
  const tools = buildAgentTools({
    gatewayConfig: input.gatewayConfig,
    nonGatewaySelections: input.nonGatewaySelections,
    ...(input.directServerIds !== undefined ? { directServerIds: input.directServerIds } : {}),
  });
  // The server names the form output already covers — never double-emit / preserve.
  const emitted = new Set<string>();
  for (const entry of tools) {
    if (entry.type === 'mcp_toolset' && typeof entry.mcp_server_name === 'string') {
      emitted.add(entry.mcp_server_name);
    }
  }
  // Preserve every SAVED connector the form cannot currently represent (and that
  // the form output didn't already emit) — its persisted grant is re-emitted
  // VERBATIM so an unrelated edit never silently strips it.
  for (const [name, entry] of input.savedToolsets) {
    if (input.representableServerNames.has(name) || emitted.has(name)) continue;
    tools.push(entry);
    emitted.add(name);
  }
  return tools;
}

/**
 * fn-74 — the whole-server `always_ask` grant the fn-73 fallback persists for a
 * connector whose per-tool catalog can't be enumerated. Used for BOTH a plain
 * non-gateway connector (in {@link buildAgentTools}) AND a DIRECT connector
 * (`supports_direct_introspection`) that is `login_required`/degraded — its tools
 * are reached outside the gateway, so HITL-on-every-call is the denylist-safe
 * control. (A gateway connector's catalog-unavailable fallback stays a bare
 * default-on grant, unchanged.)
 */
function wholeServerAlwaysAskGrant(serverId: string): Record<string, unknown> {
  return {
    type: 'mcp_toolset',
    mcp_server_name: serverId,
    configs: [],
    default_config: { enabled: true, permission_policy: { type: 'always_ask' } },
  };
}

export function authoringConfigToTools(
  config: AuthoringToolConfig,
  /**
   * fn-74 (R5) — the `mcp_server_name`s that are DIRECT
   * (`supports_direct_introspection`) connectors. For such a connector the
   * catalog-unavailable fallback is the fn-73 whole-server `always_ask` grant
   * (HITL-on-every-call), NOT a bare default-on grant; an INTROSPECTED direct
   * connector still emits its per-tool allow-list (the per-tool permissions are
   * already pinned `always_ask` by `always_allow_locked` upstream). Absent/empty
   * ⇒ every connector is a gateway connector (existing behaviour, byte-for-byte).
   */
  directServerIds: ReadonlySet<string> = EMPTY_SERVER_IDS,
): Record<string, unknown>[] {
  const tools: Record<string, unknown>[] = [];

  const builtinConfigs = Object.entries(config.nativeBuiltins)
    .filter(([name]) => name.trim().length > 0)
    .map(([name, enabled]) => ({ name: name.trim(), enabled: enabled === true }));
  tools.push(
    builtinConfigs.length > 0
      ? { type: AGENT_BUILTIN_TOOLSET_TYPE, configs: builtinConfigs }
      : { type: AGENT_BUILTIN_TOOLSET_TYPE },
  );

  for (const [rawServerId, server] of Object.entries(config.servers)) {
    const serverId = rawServerId.trim();
    if (serverId.length === 0) continue;
    // Fully-disabled connector — drop it (tools out of scope).
    if (server.disabled) continue;
    // Catalog-unavailable — whole-server grant (its tools are unknown, not
    // empty); an empty allow-list would deny every tool on the connector. For a
    // DIRECT connector (fn-74) the fallback is the fn-73 `always_ask` grant so a
    // login-required / degraded InfoTrack never becomes a non-HITL whole-server.
    if (server.catalogUnavailable) {
      tools.push(
        directServerIds.has(serverId)
          ? wholeServerAlwaysAskGrant(serverId)
          : { type: 'mcp_toolset', mcp_server_name: serverId },
      );
      continue;
    }
    const configs: Record<string, unknown>[] = [];
    for (const [toolId, permission] of Object.entries(server.tools)) {
      if (permission === 'disabled') continue;
      // Persist the QUALIFIED `tool_id` VERBATIM (fn-72) — never collapse to its
      // bare suffix, so cross-target tools with colliding bare names stay
      // distinct grants. For a DIRECT connector (fn-74) the `tool_id` IS the
      // server's NATIVE tool name (e.g. `national_titles_mcp__get_property_details`)
      // projected verbatim by the CP direct path — persisted as-is, never gateway-
      // canonicalized. The denylist (bare-name based) is the parsed suffix; it is
      // enforced upstream (panel clamp + CP re-validation), so the per-tool
      // `permission` here already reflects it (a direct tool is always `always_ask`,
      // pinned by `always_allow_locked`).
      const name = toolId.trim();
      if (name.length === 0) continue;
      configs.push({
        name,
        enabled: true,
        ...(permission !== 'always_allow' ? { permission_policy: { type: permission } } : {}),
      });
    }
    tools.push({
      type: 'mcp_toolset',
      mcp_server_name: serverId,
      // ALLOW-LIST mode — default OFF; only the listed tools are enabled.
      default_config: { enabled: false },
      configs,
    });
  }

  return tools;
}

// ── Edit-form pre-fill readers (from an agent's `config_snapshot`) ──────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Read the plain model-id string from a `config_snapshot`. The snapshot pins
 * `model` as `{ id, speed? }` (fn-67), but a legacy bare string is tolerated.
 * Returns `''` when absent — the form treats that as "needs a model".
 */
export function readModelString(snapshot: Record<string, unknown> | null): string {
  if (snapshot === null) return '';
  const model = snapshot.model;
  if (typeof model === 'string') return model;
  const record = asRecord(model);
  if (record !== null && typeof record.id === 'string') return record.id;
  return '';
}

/**
 * Read the agent's network-profile NAME from a `config_snapshot`. fn-68 pins the
 * FULL resolved `NetworkProfile` OBJECT (`{ name, webTools, networking, … }`)
 * under `config_snapshot.network_profile` — the `name` slug is the value the
 * authoring picker selects. Returns `''` when absent (legacy / agent-default),
 * which the form treats as "(agent default)".
 */
export function readNetworkProfileName(snapshot: Record<string, unknown> | null): string {
  if (snapshot === null) return '';
  const profile = asRecord(snapshot.network_profile);
  if (profile !== null && typeof profile.name === 'string') return profile.name;
  return '';
}

/** Read the referenced skill names from a `config_snapshot.dependency_summary`. */
export function readAgentSkills(snapshot: Record<string, unknown> | null): string[] {
  if (snapshot === null) return [];
  const dep = asRecord(snapshot.dependency_summary);
  if (dep === null || !Array.isArray(dep.skills)) return [];
  return dep.skills.filter((s): s is string => typeof s === 'string');
}

/**
 * Read the referenced `mcp_server_name`s from a `config_snapshot.tools[]` (the
 * `mcp_toolset` entries — the authoritative connector membership). Duplicates
 * and blanks are dropped, order preserved.
 */
export function readAgentMcpServers(snapshot: Record<string, unknown> | null): string[] {
  if (snapshot === null) return [];
  const tools = snapshot.tools;
  if (!Array.isArray(tools)) return [];
  const names: string[] = [];
  const seen = new Set<string>();
  for (const entry of tools) {
    const record = asRecord(entry);
    if (record === null || record.type !== 'mcp_toolset') continue;
    const name = typeof record.mcp_server_name === 'string' ? record.mcp_server_name.trim() : '';
    if (name.length === 0 || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

// ── Edit-form tool-baseline seed (prior config_snapshot → panel state) ───────

/**
 * The prior per-tool baselines parsed from an agent's `config_snapshot.tools[]`,
 * keyed for overlay onto a fresh draft projection so EDIT preserves the saved
 * tool config rather than re-defaulting (which could silently widen scope).
 */
export interface PriorToolConfig {
  /** `mcp_server_name` → (bare tool name → saved selection) for allow-list connectors. */
  readonly servers: ReadonlyMap<string, ReadonlyMap<string, ToolSelection>>;
  /** `mcp_server_name`s saved as a whole-server grant (every tool allowed). */
  readonly wholeServerGrant: ReadonlySet<string>;
  /** native-built-in `name` → saved enabled state. */
  readonly builtins: ReadonlyMap<string, boolean>;
}

function readPolicyType(value: unknown): string | undefined {
  const record = asRecord(value);
  return record !== null && typeof record.type === 'string' ? record.type : undefined;
}

function policyTypeToSelection(type: string | undefined): ToolSelection {
  switch (type) {
    case 'always_ask':
      return 'needs_approval';
    case 'always_deny':
      return 'disabled';
    default:
      // `always_allow` is the allow-list default when no explicit policy.
      return 'allowed';
  }
}

/**
 * Parse an agent's `config_snapshot.tools[]` into the prior baselines used to
 * seed the Edit form's tool panel. Mirrors the inverse of
 * {@link authoringConfigToTools}: an allow-list `mcp_toolset`
 * (`default_config.enabled === false`) yields per-tool selections; a bare /
 * default-on `mcp_toolset` is a whole-server grant; the built-in toolset's
 * `configs[]` yield the built-in toggles.
 */
export function readPriorToolConfig(snapshot: Record<string, unknown> | null): PriorToolConfig {
  const servers = new Map<string, Map<string, ToolSelection>>();
  const wholeServerGrant = new Set<string>();
  const builtins = new Map<string, boolean>();
  const tools = snapshot === null ? null : snapshot.tools;
  if (!Array.isArray(tools)) return { servers, wholeServerGrant, builtins };

  for (const entry of tools) {
    const record = asRecord(entry);
    if (record === null) continue;
    if (record.type === AGENT_BUILTIN_TOOLSET_TYPE) {
      if (Array.isArray(record.configs)) {
        for (const cfg of record.configs) {
          const c = asRecord(cfg);
          if (c !== null && typeof c.name === 'string' && c.name.trim().length > 0) {
            builtins.set(c.name.trim(), c.enabled === true);
          }
        }
      }
      continue;
    }
    if (record.type !== 'mcp_toolset') continue;
    const serverId =
      typeof record.mcp_server_name === 'string' ? record.mcp_server_name.trim() : '';
    if (serverId.length === 0) continue;
    const defaultConfig = asRecord(record.default_config);
    const isAllowList = defaultConfig !== null && defaultConfig.enabled === false;
    if (!isAllowList) {
      // Bare / default-on connector → whole-server grant.
      wholeServerGrant.add(serverId);
      continue;
    }
    const perTool = new Map<string, ToolSelection>();
    if (Array.isArray(record.configs)) {
      for (const cfg of record.configs) {
        const c = asRecord(cfg);
        if (c === null || typeof c.name !== 'string' || c.name.trim().length === 0) continue;
        const selection =
          c.enabled === false
            ? 'disabled'
            : policyTypeToSelection(readPolicyType(c.permission_policy));
        perTool.set(c.name.trim(), selection);
      }
    }
    servers.set(serverId, perTool);
  }
  return { servers, wholeServerGrant, builtins };
}

/**
 * Look up a tool's saved selection under ONE prior key (a saved
 * `mcp_server_name`). Returns `undefined` when this key carries no grant for the
 * tool. A whole-server grant for `key` covers ONLY tools whose `target_id` IS
 * `key` (a legacy per-target grant never extended to a sibling target); an
 * allow-list matches the QUALIFIED `tool_id` first (fn-72 saves persist
 * `<target>___<tool>`), then falls back to the BARE `tool_name` ONLY for tools of
 * this key's own target (pre-fn-72 saves persisted bare own-target names) — so a
 * bare legacy entry can never widen a colliding CROSS-target tool.
 */
function priorSelectionForKey(
  prior: PriorToolConfig,
  key: string,
  tool: { readonly tool_id: string; readonly tool_name: string; readonly target_id: string },
): ToolSelection | undefined {
  const ownTarget = tool.target_id === key;
  if (prior.wholeServerGrant.has(key)) {
    return ownTarget ? 'allowed' : undefined;
  }
  const perTool = prior.servers.get(key);
  if (perTool === undefined) return undefined;
  return perTool.get(tool.tool_id) ?? (ownTarget ? perTool.get(tool.tool_name) : undefined);
}

/**
 * Build the panel's initial state for the Edit form — `defaultPanelState` over
 * the fresh draft projection, then OVERLAY the agent's saved baselines so a save
 * preserves prior per-tool permissions. A tool that became `always_deny`- or
 * denylist-LOCKED since authoring is NEVER overridden (its safe default stands,
 * so the overlay can't re-widen a now-restricted tool).
 *
 * **fn-72 — whole-gateway aware.** The whole-gateway projection collapses every
 * federated target into ONE `smokeball` connector, so a saved baseline is matched
 * against BOTH the connector-level prior (`connector.server_id`) AND the tool's
 * TARGET-level prior (`tool.target_id`) — the latter preserves a LEGACY agent
 * that saved its targets under separate `mcp_server_name`s (e.g.
 * `mcp_server_name: 'archie'`) before the gateway was one connector.
 *
 * **No silent widening (R3/R5).** A tool ABSENT from every relevant prior seeds
 * DISABLED — never the projection baseline (which could be `always_allow` /
 * `always_ask` and thus widen scope). So opening + saving an agent can only ever
 * NARROW or preserve its grant; a newly-surfaced `forms`/`mattertypes`/`archie`
 * tool stays disabled until the author explicitly enables it. (Create uses the
 * free-for-all `defaultPanelState` baseline; this overlay is EDIT-only.)
 *
 * **fn-74 — direct whole-server grant.** A DIRECT connector's introspected tools
 * carry the SUBSERVER as `target_id` (e.g. `national_titles_mcp`), NOT the
 * connector's own `mcp_server_name` (`infotrack`). A saved direct WHOLE-SERVER
 * `always_ask` grant (`wholeServerGrant.has('infotrack')`) must therefore seed
 * EVERY tool of that connector to `needs_approval` regardless of its subserver
 * — the gateway `priorSelectionForKey` only honors a whole-server grant for the
 * tool's OWN target, which would (wrongly) seed every direct tool disabled and
 * silently narrow the saved whole-server grant to an empty allow-list once the
 * connector becomes introspectable. `directServerIds` opts a connector into this
 * direct-aware seeding; absent/empty ⇒ pure gateway behaviour (unchanged).
 */
export function applyPriorToolConfig(
  data: ConfigurableToolsResponse,
  prior: PriorToolConfig,
  directServerIds: ReadonlySet<string> = EMPTY_SERVER_IDS,
): ToolConfigPanelState {
  const base = defaultPanelState(data);
  // Preserve the null prototype of the base maps (they are keyed by EXTERNAL
  // ids) — a `{ ...base.X }` spread would reopen prototype pollution.
  const toolSelections: Record<string, ToolSelection> = Object.assign(
    Object.create(null),
    base.toolSelections,
  );
  const builtinsEnabled: Record<string, boolean> = Object.assign(
    Object.create(null),
    base.builtinsEnabled,
  );

  for (const connector of data.connectors) {
    // A DIRECT connector with a saved WHOLE-SERVER grant: its `always_ask` grant
    // covered every tool, so seed every introspected tool to `needs_approval`
    // (subserver `target_id` ≠ the connector id, so the gateway own-target rule
    // can't see it). Preserves the grant; never widens (always_ask) or strips.
    const directWholeServer =
      directServerIds.has(connector.server_id) && prior.wholeServerGrant.has(connector.server_id);
    for (const tool of connector.tools) {
      // Never re-widen a now-locked tool — keep its safe default.
      if (tool.always_deny_locked || tool.denylist_locked) continue;
      if (directWholeServer) {
        toolSelections[tool.tool_id] = 'needs_approval';
        continue;
      }
      // Consult the CONNECTOR-level prior first, then the TARGET-level prior
      // (legacy per-target saves keyed by `mcp_server_name == target`). The
      // target-level check is skipped when it would re-check the same key.
      const saved =
        priorSelectionForKey(prior, connector.server_id, tool) ??
        (tool.target_id !== connector.server_id
          ? priorSelectionForKey(prior, tool.target_id, tool)
          : undefined);
      // ABSENT from every relevant prior → disabled (no silent widening).
      toolSelections[tool.tool_id] = saved ?? 'disabled';
    }
  }

  for (const builtin of data.native_builtins) {
    const saved = prior.builtins.get(builtin.name);
    if (saved !== undefined) builtinsEnabled[builtin.name] = saved;
  }

  return {
    connectorsDisabled: Object.assign(Object.create(null), base.connectorsDisabled),
    toolSelections,
    builtinsEnabled,
  };
}
